import React, { useEffect, useState, useCallback } from "react"
import { Col, Row, Card } from "react-bootstrap"
import CardGrid from "components/shared/CardGrid/CardGrid"
import FilterBarModule from "components/shared/FilterBarModule"
import "./ApplicationOffers.scss"
import {statesOptions} from "../../constants/constantVariables";

function ApplicationOffers(props) {
  const {
    applicationOffersList,
    getApplicationOffersList,
    totalRecords,
    totalPages,
    history
  } = props

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [searchParameter, setSearchParameter] = useState('uuid')
  const [pagination, setPagination] = useState({ page: 0, sizePerPage: 20 })
  const [filter, setFilter] = useState({})

  const filterColumns = [
    {
      id: 'uuid',
      type: 'text',
      label: 'Offer #',
      defaultValue: ''
    },
    {
      id: 'status',
      type: 'dropdown',
      label: 'Status',
      options: [
        { value: 'NEW', label: 'New' },
        { value: 'VIEWED', label: 'Viewed' },
        { value: 'PURCHASED', label: 'Purchased' },
      ]
    },
    {
      id: 'state',
      type: 'dropdown',
      label: 'State',
      options: statesOptions.USA
    },
    {
      id: 'businessType',
      type: 'dropdown',
      label: 'Business Type',
      options: [
        { value: 'Corporations', label: 'Corporations' },
        { value: 'S Corporations', label: 'S Corporations' },
        { value: 'Sole Proprietorships', label: 'Sole Proprietorships' },
        { value: 'Partnerships', label: 'Partnerships' },
        { value: 'Limited Liability Company (LLC)', label: 'Limited Liability Company (LLC)' },
        { value: 'Limited Liability Corporation', label: 'Limited Liability Corporation' }
      ]
    },
    {
      id: 'industryType',
      type: 'dropdown',
      label: 'Industry Type',
      options: [
        { value: 'Transportation', label: 'Transportation' },
        { value: 'Trucking and Freight', label: 'Trucking and Freight' },
        { value: 'General Factoring', label: 'General Factoring' },
        { value: 'Construction', label: 'Construction' },
        { value: 'Staffing', label: 'Staffing' },
        { value: 'Oil and Gas', label: 'Oil and Gas' },
        { value: 'Manufacturing', label: 'Manufacturing' },
        { value: 'Food and Beverage', label: 'Food and Beverage' },
        { value: 'General', label: 'General' },
        { value: 'Healthcare', label: 'Healthcare' },
        { value: 'Other', label: 'Other' },
        { value: 'Professional Services', label: 'Professional Services' },
        { value: 'Wholesale / Distribution', label: 'Wholesale / Distribution' },
        { value: 'Government Contractors', label: 'Government Contractors' },
      ]
    }
  ]

  const getApplicationOffers = useCallback((paginationParams = {}, filters = {}) => {
    setLoading(true)
    getApplicationOffersList(paginationParams, filters).finally(() =>
        setLoading(false)
    )
  }, [getApplicationOffersList])

  useEffect(() => {
    const filterPayload = {
      ...(search && { [searchParameter]: search }),
      ...filter
    }
    getApplicationOffers(pagination, filterPayload)
  }, [getApplicationOffers, search, pagination, filter, searchParameter])

  const handleFilterApply = (filterParams) => {
    setPagination({ page: 0, sizePerPage: 20 })
    setFilter(filterParams)
  }

  const handlePaginationChange = (paginationParams) => {
    setPagination({
      page: paginationParams.page,
      sizePerPage: paginationParams.limit
    })
  }

  const getStatusStyle = (status) => {
    const statusColors = {
      'New': {
        background: 'linear-gradient(135deg, #e8f4fd 0%, #d0e8f9 100%)',
        color: '#0d47a1'
      },
      'In Progress': {
        background: 'linear-gradient(135deg, #fff3cd 0%, #ffe8a3 100%)',
        color: '#6d4c41'
      },
      'Completed': {
        background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
        color: '#1b5e20'
      },
      'Cancelled': {
        background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
        color: '#b71c1c'
      },
      'Viewed': {
        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
        color: 'var(--rox-lilac-ink)'
      }
    }
    return statusColors[status] || {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      color: 'var(--neutral-700)'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '---'
    try {
      // Parse using UTC to avoid timezone conversion issues
      const date = new Date(dateString)

      // Get UTC components to avoid timezone shift
      const year = date.getUTCFullYear()
      const month = date.getUTCMonth()
      const day = date.getUTCDate()

      // Create a new date in local timezone with UTC values
      const localDate = new Date(year, month, day)

      return localDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return '---'
    }
  }

  const handleCardClick = (offer) => {
    if (offer.status === 'Purchased' || offer.status === 'PURCHASED') {
      history.push(`/applicant/${offer.purchasedUuid}`)
    } else {
      history.push(`/applicant-offer/${offer.uuid}`)
    }
  }

  const renderApplicationOfferCard = (offer) => {
    const statusStyle = getStatusStyle(offer.status)

    return (
        <Card className="offer-card">
          <Card.Body>
            <div className="card-title-link">
              <Card.Title>Offer #{offer.uuid.substring(0, 8)}</Card.Title>
            </div>

            <div className="card-field">
              <span className="field-label">Days in Market</span>
              <span className="field-value">{offer.daysInMarket} days</span>
            </div>

            <div className="card-field">
              <span className="field-label">Provider Reasoning</span>
              <span className="field-value">{offer.providerReasoning || '---'}</span>
            </div>

            <div className="card-field">
              <span className="field-label">State</span>
              <span className="field-value">{offer.state || '---'}</span>
            </div>

            <div className="card-field">
              <span className="field-label">Business Type</span>
              <span className="field-value">{offer.businessType || '---'}</span>
            </div>

            <div className="card-field">
              <span className="field-label">Industry Type</span>
              <span className="field-value">{offer.industryType || '---'}</span>
            </div>

            <div className="card-field">
              <span className="field-label">Formation Date</span>
              <span className="field-value">{formatDate(offer.formationDate)}</span>
            </div>

            <div className="card-field">
              <span className="field-label">Status</span>
              <div
                  className="status-badge"
                  style={{
                    background: statusStyle.background,
                    color: statusStyle.color
                  }}
              >
                {offer.status || '---'}
              </div>
            </div>
          </Card.Body>
        </Card>
    )
  }

  return (
      <div id="application-offers">
        <Row>
          <Col xs={4} className="text-left">
            <h4>Application Offers</h4>
          </Col>
          <Col xs={8} className="text-right">
            <div className="buttonContainer">
            </div>
          </Col>
        </Row>

        <FilterBarModule
            filters={filterColumns}
            filterParams={handleFilterApply}
            onSearch={setSearch}
            placeHolder="Search"
        />

        <CardGrid
            data={applicationOffersList}
            isLoading={loading}
            showPagination={true}
            showLimitOptions={true}
            totalPages={totalPages}
            totalRecords={totalRecords}
            currentPage={pagination.page}
            paginationLimit={pagination.sizePerPage}
            onChangePagination={handlePaginationChange}
            renderCard={renderApplicationOfferCard}
            onCardClick={handleCardClick}
            keyField="uuid"
            className="application-offers-grid"
        />
      </div>
  )
}

export default ApplicationOffers